import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import UsageProgressBar from "../../components/UsageProgressBar";
import ApproveTenantModal from "../../components/ApproveTenantModal";
import {
  currentPeriod,
  deleteTenant,
  fetchPlatformPendingTenants,
  fetchPlatformTenants,
  fetchTenantUsageSummary,
  reactivateTenant,
  rejectTenant,
  suspendTenant,
} from "../../lib/opsGraphql";

function RejectModal({ tenant, onClose, onConfirm, loading }) {
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("other");
  return (
    <div className="ops-modal-backdrop">
      <div className="ops-modal">
        <h3>Rechazar {tenant.name}</h3>
        <label>
          Motivo *
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} required />
        </label>
        <label>
          Categoría
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="duplicate">duplicate</option>
            <option value="invalid_data">invalid_data</option>
            <option value="commercial">commercial</option>
            <option value="other">other</option>
          </select>
        </label>
        <div className="ops-actions">
          <button type="button" className="ops-btn secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="ops-btn danger"
            disabled={loading || !reason.trim()}
            onClick={() => onConfirm(reason.trim(), category)}
          >
            Rechazar
          </button>
        </div>
      </div>
    </div>
  );
}

function sortByConsumption(tenants, usageMap) {
  return [...tenants].sort((a, b) => {
    const ua = usageMap[a.tenant_id];
    const ub = usageMap[b.tenant_id];
    const scoreA = ua?.usage_pct ?? ua?.documents_processed ?? 0;
    const scoreB = ub?.usage_pct ?? ub?.documents_processed ?? 0;
    return scoreB - scoreA;
  });
}

export default function TenantsList() {
  const [tenants, setTenants] = useState([]);
  const [pending, setPending] = useState([]);
  const [usageMap, setUsageMap] = useState({});
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const period = currentPeriod();

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const tenantFilter = {};
      if (filter.trim()) tenantFilter.search = filter.trim();
      if (statusFilter) tenantFilter.status = statusFilter;
      const [all, pend] = await Promise.all([
        fetchPlatformTenants(Object.keys(tenantFilter).length ? tenantFilter : null),
        fetchPlatformPendingTenants(),
      ]);
      setTenants(all);
      setPending(pend);

      const withUsage = all.filter((t) => t.status === "active");
      const usageEntries = await Promise.all(
        withUsage.map(async (t) => {
          try {
            return [t.tenant_id, await fetchTenantUsageSummary(t.tenant_id, period)];
          } catch {
            return [t.tenant_id, null];
          }
        })
      );
      setUsageMap(Object.fromEntries(usageEntries));
    } catch (e) {
      setError(e.message || "No se pudieron cargar tenants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [filter, statusFilter, period]);

  const sortedTenants = useMemo(
    () => sortByConsumption(tenants, usageMap),
    [tenants, usageMap]
  );

  const runAction = async (label, fn) => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      await fn();
      setInfo(label);
      await refresh();
    } catch (e) {
      setError(e.message || "Error en la operación");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (tenant) => {
    const slug = tenant.slug || tenant.tenant_id;
    const typed = window.prompt(`Para eliminar "${tenant.name}", escribí el slug: ${slug}`);
    if (typed !== slug && typed !== tenant.tenant_id) return;
    runAction(`${tenant.tenant_id} eliminado`, () =>
      deleteTenant(tenant.tenant_id, typed, "Eliminado desde backoffice")
    );
  };

  return (
    <>
      {error && <p className="ops-error">{error}</p>}
      {info && <p className="ops-info">{info}</p>}

      <div className="ops-card">
        <h2>Cola de aprobación ({pending.length})</h2>
        {pending.length === 0 ? (
          <p>Sin tenants pendientes.</p>
        ) : (
          <table className="ops-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Estado</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((t) => (
                <tr key={t.tenant_id}>
                  <td>
                    <strong>{t.name || t.tenant_id}</strong>
                    <br />
                    <small>{t.tenant_id}</small>
                  </td>
                  <td>
                    <span className="ops-badge warning">{t.status}</span>
                  </td>
                  <td>{t.created_at || "—"}</td>
                  <td>
                    <div className="ops-actions">
                      <button
                        type="button"
                        className="ops-btn"
                        disabled={loading}
                        onClick={() => setApproveTarget(t)}
                      >
                        Aprobar
                      </button>
                      <button
                        type="button"
                        className="ops-btn danger"
                        disabled={loading}
                        onClick={() => setRejectTarget(t)}
                      >
                        Rechazar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="ops-card">
        <h2>Todos los tenants</h2>
        <p className="ops-subtitle">Ordenados por mayor consumo de documentos del mes.</p>
        <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            type="search"
            placeholder="Buscar por nombre o id…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && refresh()}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="rejected">rejected</option>
          </select>
          <button type="button" className="ops-btn secondary" onClick={refresh}>
            Buscar
          </button>
        </div>
        <table className="ops-table ops-table-usage">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Docs mes</th>
              <th>Fallback tokens</th>
              <th>Plan</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedTenants.map((t) => {
              const usage = usageMap[t.tenant_id];
              const fallbackLimit =
                usage?.fallback_tokens_included && usage.fallback_tokens_included > 0
                  ? usage.fallback_tokens_included
                  : null;

              return (
                <tr key={t.tenant_id}>
                  <td>
                    <Link to={`/tenants/${t.tenant_id}`}>
                      <strong>{t.name}</strong>
                    </Link>
                    <br />
                    <small>{t.tenant_id}</small>
                  </td>
                  <td style={{ minWidth: 200 }}>
                    {usage ? (
                      <UsageProgressBar used={usage.documents_processed} limit={usage.documents_limit} />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={{ minWidth: 200 }}>
                    {usage ? (
                      <UsageProgressBar
                        used={usage.fallback_tokens_used}
                        limit={fallbackLimit}
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <Link to={`/planes?tenant=${t.tenant_id}`} className="ops-link">
                      Gestionar
                    </Link>
                  </td>
                  <td>
                    <span
                      className={`ops-badge ${
                        t.status === "suspended" ? "danger" : t.status === "active" ? "" : "warning"
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td>
                    <div className="ops-actions">
                      {t.status === "active" && (
                        <button
                          type="button"
                          className="ops-btn danger"
                          disabled={loading}
                          onClick={() => {
                            if (!window.confirm(`¿Suspender ${t.tenant_id}?`)) return;
                            runAction(`${t.tenant_id} suspendido`, () =>
                              suspendTenant(t.tenant_id, "other", "Suspendido desde ops")
                            );
                          }}
                        >
                          Suspender
                        </button>
                      )}
                      {t.status === "suspended" && (
                        <button
                          type="button"
                          className="ops-btn"
                          disabled={loading}
                          onClick={() =>
                            runAction(`${t.tenant_id} reactivado`, () => reactivateTenant(t.tenant_id))
                          }
                        >
                          Reactivar
                        </button>
                      )}
                      {(t.is_demo || t.status === "rejected" || t.status?.startsWith("pending")) && (
                        <button
                          type="button"
                          className="ops-btn danger"
                          disabled={loading}
                          onClick={() => handleDelete(t)}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && <p className="ops-loading">Cargando…</p>}
      </div>

      {rejectTarget && (
        <RejectModal
          tenant={rejectTarget}
          loading={loading}
          onClose={() => setRejectTarget(null)}
          onConfirm={(reason, category) =>
            runAction(`${rejectTarget.tenant_id} rechazado`, async () => {
              await rejectTenant(rejectTarget.tenant_id, reason, category);
              setRejectTarget(null);
            })
          }
        />
      )}

      {approveTarget && (
        <ApproveTenantModal
          tenant={approveTarget}
          loading={loading}
          onClose={() => setApproveTarget(null)}
          onSuccess={async () => {
            setApproveTarget(null);
            setInfo(`${approveTarget.tenant_id} aprobado con plan asignado`);
            await refresh();
          }}
        />
      )}
    </>
  );
}
